'use client';

import type { ReactNode, FC } from 'react';
import type { SelectedDate } from '@yanolja-rnd/nol-design-system';
import type { DeviceType } from '@weberus/shared-types/src';
import { useMemo, useState } from 'react';
import { DayPickerWeeks, DayPicker, cn, TextButton } from '@yanolja-rnd/nol-design-system';
import { IcCalendarcheckFill, IcInfoFill } from '@yanolja-rnd/nol-icons';
import { format, differenceInDays } from 'date-fns';
import { ko } from 'date-fns/locale';
import { useQuery } from '@tanstack/react-query';
import throttle from 'lodash-es/throttle';
import { toZonedTime } from 'date-fns-tz';
import { isInYaTime } from '@/app/_shared/utils/date';
import { fetchCalendarHolidays } from '@/app/_components/common/date-capacity-select-bottom-sheet/api';
import { clientFetcher } from '@/app/_shared/utils/fetcher/client-fetcher';
import { AccordionSelectBox } from './accordion-select-box';

export interface DateSelectProps {
  isOpen: boolean;
  /** 해외 숙소인 경우 */
  isGlobal?: boolean;
  /** 선택된 날짜 */
  selectedDate?: SelectedDate;
  /** 최대 날짜 선택 가능 일수 */
  maxDuration?: number;
  /** 날짜 선택 시 알림 사항 텍스트 */
  noticeText?: ReactNode;
  description?: ReactNode;
  className?: string;
  /** 날짜 오픈 시 */
  onOpenChange: (isOpen: boolean) => void;
  /** 날짜 선택 시 */
  onDateChange: (date: SelectedDate) => void;
  deviceType?: DeviceType;
}

const ONE_DAY_TIME = 1000 * 60 * 60 * 24;

export const DateSelect: FC<DateSelectProps> = (props) => {
  const {
    isOpen = false,
    isGlobal = false,
    maxDuration,
    deviceType,
    selectedDate,
    noticeText,
    description,
    className,
    onOpenChange,
    onDateChange,
  } = props;
  const isPc = deviceType === 'etc';

  const [isScrolled, setIsScrolled] = useState(false);

  // 캘린더 공휴일 조회, 실패에 따로 에러처리를 하지 않음
  const { data } = useQuery({
    queryKey: ['CALENDAR_HOLIDAYS'],
    queryFn: () => fetchCalendarHolidays(clientFetcher)({ durationMonth: maxDuration ? (maxDuration % 30) + 1 : 12 }),
    staleTime: ONE_DAY_TIME,
    gcTime: ONE_DAY_TIME,
  });

  const holidays = useMemo(() => data?.map((holiday) => new Date(holiday.date)) ?? [], [data]);

  const useYanoljaTime = useMemo(() => !isGlobal && isInYaTime(toZonedTime(new Date(), 'Asia/Seoul')), [isGlobal]);

  const stayDurationText = useMemo(() => {
    if (!selectedDate) return '';

    const [checkIn, checkOut] = selectedDate;
    if (!checkIn) return '';

    const stayDuration = checkOut ? differenceInDays(checkOut, checkIn) : 1;

    const formatWithDay = (date: Date) => {
      const day = format(date, 'EEE', { locale: ko });
      return `${format(date, 'MM.dd')}(${day})`;
    };

    return checkOut
      ? `${formatWithDay(checkIn)}~${formatWithDay(checkOut)} • ${stayDuration}박`
      : `${formatWithDay(checkIn)} • ${stayDuration}박`;
  }, [selectedDate]);

  const handleScroll = throttle((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    setIsScrolled(target.scrollTop > 0);
  }, 100);

  return (
    <AccordionSelectBox
      isOpen={isOpen}
      className={className}
      deviceType={deviceType}
      onHeaderClick={() => onOpenChange(!isOpen)}>
      {{
        header: (
          <div className="flex size-full items-center gap-6">
            <IcCalendarcheckFill className={cn('shrink-0', isPc ? 'size-24' : 'size-20')} />
            <span
              className={cn('w-full truncate', isPc ? 'typography-subtitle-18-bold' : 'typography-subtitle-16-bold')}>
              {stayDurationText}
            </span>
            {!isOpen && (
              <TextButton theme="underline" className="shrink-0">
                변경하기
              </TextButton>
            )}
          </div>
        ),
        additonalHeader: (
          <div
            className={cn('z-10 shrink-0 border-b border-line-neutral-weak2 bg-static-white', {
              'border-transparent shadow-2': isScrolled,
            })}
            style={{
              clipPath: isScrolled ? 'inset(0px 1px -4px 1px)' : undefined,
            }}>
            <DayPickerWeeks className={isPc ? 'px-12' : 'px-8'} />
            {!!noticeText && (
              <div className="flex items-center justify-center gap-4 bg-fill-neutral-weak1 p-12">
                <IcInfoFill className={isPc ? 'size-18' : 'size-16'} />
                <span className={isPc ? 'typography-subtitle-16-regular' : 'typography-body-12-regular'}>
                  {noticeText}
                </span>
              </div>
            )}
          </div>
        ),
        body: (
          <div className="flex h-full flex-col">
            {isOpen && (
              <div className="h-full bg-static-white">
                {!!description && (
                  <div
                    className={cn(
                      'pt-4 text-text-neutral-sub',
                      isPc ? 'px-12 typography-body-14-regular' : 'px-8 typography-caption-11-regular',
                    )}>
                    {description}
                  </div>
                )}
                <DayPicker
                  holidays={holidays}
                  monthClassName={cn(isPc ? 'px-12' : 'px-8')}
                  deviceType={deviceType}
                  selectedDate={selectedDate}
                  options={{
                    dayDescriptions: ['체크인', '체크아웃'],
                    maxDuration,
                    useYanoljaTime,
                    preLoadingThreshold: isPc ? 700 : 500,
                  }}
                  onDateChange={(dates: SelectedDate) => onDateChange(dates)}
                  onScroll={handleScroll}
                />
              </div>
            )}
          </div>
        ),
      }}
    </AccordionSelectBox>
  );
};
